/**
 * DSC Travel — Storytelling Typography v5
 * 
 * Sistema de 3 níveis por tela:
 *   1. Entry Point (serif GRANDE, branco, uppercase)
 *   2. Body (sans light, prata)
 *   3. CTA (botão accent, verbo concreto)
 * 
 * + Meta (fora da hierarquia, 22% opacidade)
 * 
 * Copiar e aplicar. Não adaptar. Não interpretar.
 */

import { colors, fonts, tracking } from './tokens';

// ─── Nível 1: Entry Point ───
export const entryPoint = {
  fontFamily: fonts.display,
  fontWeight: 400,
  fontSize: 'clamp(2.2rem, 7vw, 2.8rem)',
  lineHeight: 1.08,
  color: colors.white,
  letterSpacing: tracking.display,
  textTransform: 'uppercase',
  textShadow: '0 0 0.6px rgba(240, 240, 248, 0.35)',
  margin: 0,
};

// ─── Nível 2: Body ───
export const screenBody = {
  fontFamily: fonts.body,
  fontWeight: 300,
  fontSize: 'clamp(0.92rem, 2.7vw, 1rem)',
  lineHeight: 1.75,
  color: colors.silver,
  margin: 0,
};

// ─── Nível 3: CTA ───
export const ctaButton = {
  display: 'block',
  width: '100%',
  padding: '16px 24px',
  border: 'none',
  borderRadius: '50px',
  background: colors.accent,
  color: colors.deep,
  fontFamily: fonts.body,
  fontSize: '0.85rem',
  fontWeight: 600,
  letterSpacing: tracking.wide,
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'transform 0.2s',
};

// ─── Meta (fora da hierarquia) ───
export const screenMeta = {
  fontFamily: fonts.body,
  fontSize: '0.7rem',
  fontWeight: 500,
  letterSpacing: tracking.wider,
  textTransform: 'uppercase',
  color: 'rgba(255, 255, 255, 0.22)',
  margin: 0,
};

// ─── Screen base ───
export const screenBase = {
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: '0 32px 44px',
  fontFamily: fonts.body,
};

// ─── Screen background ───
export const screenBg = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  overflow: 'hidden',
};

// ─── Screen background overlay (gradient fade) ───
export const screenBgOverlay = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '70%',
  background: `linear-gradient(to top, ${colors.deepBg}, transparent)`,
};

// ─── Screen content ───
export const screenContent = {
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

// ─── Icon button (voltar, compartilhar) ───
export const iconBtn = {
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.08)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.white,
  fontSize: '16px',
  border: 'none',
  cursor: 'pointer',
};

// ─── Top bar ───
export const topBar = {
  position: 'absolute',
  top: '20px',
  left: '28px',
  right: '28px',
  display: 'flex',
  justifyContent: 'space-between',
  zIndex: 2,
};

// ─── Step dots ───
export const stepDots = {
  display: 'flex',
  gap: '6px',
  justifyContent: 'center',
  alignItems: 'center',
};

export const stepDot = {
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.12)',
};

export const stepDotActive = {
  width: '20px',
  height: '6px',
  borderRadius: '3px',
  background: colors.accent,
};

// ─── Gradientes por tela ───
export const gradients = {
  screen1: {
    base: `linear-gradient(180deg, ${colors.deepBg} 0%, ${colors.deep} 100%)`,
    bg: 'linear-gradient(135deg, #0b0969 0%, #060550 50%, #09077d 100%)',
    bgHeight: '52%',
  },
  screen2: {
    base: `linear-gradient(180deg, ${colors.deepBg} 0%, ${colors.deep} 100%)`,
    bg: 'linear-gradient(135deg, #09077d 0%, #0b0969 50%, #060550 100%)',
    bgHeight: '48%',
  },
  screen3: {
    base: `linear-gradient(180deg, ${colors.deepBg} 0%, ${colors.deep} 100%)`,
    bg: 'linear-gradient(135deg, #060550 0%, #0b0969 50%, #09077d 100%)',
    bgHeight: '40%',
  },
  screen4: {
    base: `linear-gradient(180deg, ${colors.deepBg} 0%, ${colors.deep} 100%)`,
  },
};

// ─── Tela 4: Trip Meta ───
export const tripMeta = {
  fontFamily: fonts.body,
  fontSize: '0.8rem',
  fontWeight: 400,
  color: colors.slate,
  letterSpacing: tracking.wide,
  marginTop: '6px',
  marginBottom: '28px',
};

export const tripMetaSeparator = {
  color: 'rgba(255, 255, 255, 0.12)',
  margin: '0 8px',
};

// ─── Tela 4: Card Incluído ───
export const includesCard = {
  background: 'rgba(255, 255, 255, 0.03)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  borderRadius: '16px',
  padding: '18px',
  marginBottom: '16px',
};

export const cardLabel = {
  fontFamily: fonts.body,
  fontSize: '0.7rem',
  fontWeight: 500,
  letterSpacing: tracking.wider,
  textTransform: 'uppercase',
  color: colors.slate,
  marginBottom: '14px',
  margin: '0 0 14px 0',
};

export const includesItem = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
};

export const iconCircle = {
  width: '38px',
  height: '38px',
  borderRadius: '50%',
  background: colors.accentSoft,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const itemTitle = {
  fontFamily: fonts.body,
  fontSize: '0.85rem',
  fontWeight: 500,
  color: colors.white,
  margin: '0 0 2px 0',
};

export const itemRoute = {
  fontFamily: fonts.body,
  fontSize: '0.75rem',
  fontWeight: 300,
  color: colors.slate,
  margin: 0,
};

// ─── Tela 4: Card de Preço ───
// CRÍTICO: Usa font-body (Raleway), NÃO font-display. Preço é dado, não título.
export const priceCard = {
  background: 'linear-gradient(135deg, rgba(80, 207, 173, 0.06), rgba(80, 207, 173, 0.02))',
  border: '1px solid rgba(80, 207, 173, 0.1)',
  borderRadius: '16px',
  padding: '24px',
  textAlign: 'center',
  marginBottom: '24px',
};

export const priceLabel = {
  fontFamily: fonts.body,
  fontSize: '0.7rem',
  fontWeight: 500,
  letterSpacing: tracking.wider,
  textTransform: 'uppercase',
  color: colors.slate,
  margin: '0 0 6px 0',
};

export const priceValue = {
  fontFamily: fonts.body,  // SEM SERIF — é dado, não título
  fontWeight: 400,
  fontSize: 'clamp(1.8rem, 6vw, 2.2rem)',
  color: colors.white,
  letterSpacing: '-0.01em',
  margin: '0 0 4px 0',
};

export const priceInstallment = {
  fontFamily: fonts.body,
  fontSize: '0.75rem',
  fontWeight: 300,
  color: colors.slate,
  margin: 0,
};

// ─── Logo da Tela 3 ───
export const brandLogoStack = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
};

export const brandLogoSymbol = {
  height: 'clamp(40px, 10vw, 52px)',
  width: 'auto',
  objectFit: 'contain',
};

export const brandLogoLetters = {
  height: 'clamp(32px, 8vw, 44px)',
  width: 'auto',
  objectFit: 'contain',
};
